import React from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Search, Clock, BookOpen, Star } from 'lucide-react';

// อัลกอริทึม React Query:
// 1. useQuery รับ key และ fetch function
// 2. ถ้า key นี้เคย fetch และ cache ยัง fresh → ใช้ cache
// 3. ถ้า cache stale → background refetch
// 4. isLoading = true ถ้ายังไม่มี data (ครั้งแรก)

const fetchCourses = async ({ queryKey }) => {
  const [, params] = queryKey;
  const res = await axios.get('/courses', { params });
  return res.data;
};

export default function CourseListPage() {
  const [search, setSearch] = React.useState('');
  const [category, setCategory] = React.useState('');
  const [level, setLevel] = React.useState('');
  const [page, setPage] = React.useState(1);

  const { data, isLoading, error } = useQuery(
    ['courses', { page, search, category, level }],
    fetchCourses,
    {
      keepPreviousData: true,
      refetchInterval: 5000,
      refetchOnWindowFocus: true,
      staleTime: 0,
    } // โหลดใหม่ทุก 5 วินาที และเมื่อกลับมาที่แท็บนี้
  );

  const courses = data?.data || [];
  const pagination = data?.pagination;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-6">คอร์สเรียนทั้งหมด</h1>
      
      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-6">
        <div className="relative flex-grow max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="ค้นหาคอร์ส..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
          />
        </div>
        <select value={level} onChange={(e) => setLevel(e.target.value)} className="px-4 py-2 border rounded-lg">
          <option value="">ระดับทั้งหมด</option>
          <option value="beginner">ผู้เริ่มต้น</option>
          <option value="intermediate">ระดับกลาง</option>
          <option value="advanced">ระดับสูง</option>
        </select>
        <select value={category} onChange={(e) => setCategory(e.target.value)} className="px-4 py-2 border rounded-lg">
          <option value="">หมวดหมู่ทั้งหมด</option>
          <option value="1">เทคโนโลยี</option>
          <option value="2">ธุรกิจ</option>
          <option value="3">ภาษา</option>
        </select>
      </div>

      {/* Course Grid */}
      {isLoading ? (
        <div className="text-center py-12">กำลังโหลด...</div>
      ) : error ? (
        <div className="text-center py-12 text-red-500">เกิดข้อผิดพลาด</div>
      ) : (
        <div className="grid md:grid-cols-3 gap-6">
          {courses.map(course => (
            <CourseCard key={course.id} course={course} />
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-8">
          {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map(p => (
            <button
              key={p}
              onClick={() => setPage(p)}
              className={`px-4 py-2 rounded-lg transition ${
                page === p ? 'bg-blue-600 text-white' : 'bg-gray-100 hover:bg-gray-200'
              }`}
            >
              {p}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function CourseCard({ course }) {
  return (
    <Link to={`/courses/${course.id}`} className="bg-white rounded-xl shadow-sm hover:shadow-lg transition border border-gray-100 overflow-hidden group">
      <div className="bg-gray-200 h-48 flex items-center justify-center group-hover:bg-gray-300 transition">
        {course.thumbnail ? (
          <img src={course.thumbnail} alt={course.title} className="w-full h-full object-cover" />
        ) : (
          <BookOpen className="text-gray-400" size={48} />
        )}
      </div>
      <div className="p-5">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded-full">{course.level}</span>
          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">{course.category_name}</span>
        </div>
        <h3 className="font-bold text-lg mb-1 line-clamp-2">{course.title}</h3>
        <p className="text-sm text-gray-500 mb-3">{course.instructor_name}</p>
        <div className="flex items-center justify-between text-sm text-gray-500">
          <div className="flex items-center gap-1">
            <Clock size={14} />
            <span>{course.duration} ชม.</span>
          </div>
          <div className="flex items-center gap-1">
            <Star size={14} className="text-yellow-500" />
            <span>{course.rating || '4.5'}</span>
          </div>
        </div>
        <div className="mt-3 text-blue-600 font-bold">
          {course.price === 0 ? 'ฟรี' : `฿${course.price}`}
        </div>
      </div>
    </Link>
  );
}
